/**
 * One browser window's live connection to one terminal: WebSocket /api/term/:id.
 * Output comes down numbered (a 'replay' resets the screen); keystrokes go up numbered.
 * Reconnects with backoff unless the hub said the terminal is gone or not yours to watch.
 */
import { TERM_WS_CLOSE, tailAppend } from '@orgus/protocol';
import type { TermClientMessage, TermServerMessage, TermSummary, UiErrorShape } from '@orgus/protocol';
import type { TerminalSource } from '@orgus/ui';

export type SocketPhase = 'connecting' | 'open' | 'closed' | 'error';

export interface TermConnState {
  phase: SocketPhase;
  term: TermSummary | null;
  you: { actor: string; canWrite: boolean; canForce: boolean } | null;
  /** Why the socket is not usable (phase error), or the last notice (a lost keystroke). */
  error: UiErrorShape | null;
  exitCode: number | null;
}

type Listener = (data: string, reset: boolean) => void;

interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

export interface TermConnectionOptions {
  url?: string;
  createSocket?: (url: string) => SocketLike;
  onState: (s: TermConnState) => void;
  /** Notices that are not fatal (lost keystrokes, "you are watching"). */
  onNotice?: (e: UiErrorShape) => void;
  /** Reconnect delays, for tests. */
  backoff?: (attempt: number) => number;
}

const FINAL_CODES = new Set<number>([TERM_WS_CLOSE.notFound, TERM_WS_CLOSE.forbidden, TERM_WS_CLOSE.ended]);
const SCREEN_MAX = 512 * 1024;

export function termSocketUrl(termId: string, loc: Pick<Location, 'protocol' | 'host'> = window.location): string {
  return `${loc.protocol === 'https:' ? 'wss' : 'ws'}://${loc.host}/api/term/${encodeURIComponent(termId)}`;
}

export class TermConnection implements TerminalSource {
  private readonly termId: string;
  private readonly opts: TermConnectionOptions;
  private ws: SocketLike | null = null;
  private stopped = false;
  private attempt = 0;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;
  private seq = 0;
  private lastOut = 0;
  private screen = '';
  private readonly listeners = new Set<Listener>();
  private st: TermConnState = { phase: 'connecting', term: null, you: null, error: null, exitCode: null };

  constructor(termId: string, opts: TermConnectionOptions) {
    this.termId = termId;
    this.opts = opts;
  }

  get state(): TermConnState {
    return this.st;
  }

  private set(patch: Partial<TermConnState>): void {
    this.st = { ...this.st, ...patch };
    this.opts.onState(this.st);
  }

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.retry) clearTimeout(this.retry);
    if (this.ping) clearInterval(this.ping);
    this.retry = null;
    this.ping = null;
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.close(1000, 'closed');
    }
  }

  private connect(): void {
    const url = this.opts.url ?? termSocketUrl(this.termId);
    let ws: SocketLike;
    try {
      ws = this.opts.createSocket ? this.opts.createSocket(url) : (new WebSocket(url) as unknown as SocketLike);
    } catch {
      this.set({ phase: 'error', error: { what: 'Could not open the terminal connection.', why: 'The browser refused the address.', next: 'Reload the page and try again.' } });
      return;
    }
    this.ws = ws;
    this.seq = 0;
    this.set({ phase: 'connecting' });
    ws.onopen = () => {
      this.attempt = 0;
      if (this.ping) clearInterval(this.ping);
      this.ping = setInterval(() => this.send({ t: 'ping' }), 20_000);
    };
    ws.onmessage = (ev) => {
      let msg: TermServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as TermServerMessage;
      } catch {
        return;
      }
      this.onMessage(msg);
    };
    ws.onerror = () => undefined;
    ws.onclose = (ev) => {
      if (this.ping) clearInterval(this.ping);
      this.ping = null;
      if (this.ws !== ws || this.stopped) return;
      this.ws = null;
      if (FINAL_CODES.has(ev.code)) {
        if (ev.code === TERM_WS_CLOSE.ended || this.st.term?.state === 'exited') return this.set({ phase: 'closed' });
        // The hub sent the reason as an 'error' message just before closing.
        return this.set({
          phase: 'error',
          error: this.st.error ?? { what: 'The terminal closed.', why: ev.reason || 'The hub ended the connection.', next: 'Open the terminal again.' },
        });
      }
      this.set({ phase: 'connecting' });
      const delay = (this.opts.backoff ?? ((n) => Math.min(10_000, 500 * 2 ** n)))(this.attempt++);
      this.retry = setTimeout(() => this.connect(), delay);
    };
  }

  private onMessage(msg: TermServerMessage): void {
    switch (msg.t) {
      case 'hello':
        this.set({ phase: 'open', term: msg.term, you: msg.you, error: null, exitCode: msg.term.exitCode });
        return;
      case 'replay':
        this.lastOut = msg.seq;
        this.screen = msg.data;
        for (const l of this.listeners) l(msg.data, true);
        return;
      case 'data':
        if (msg.seq <= this.lastOut) return;
        this.lastOut = msg.seq;
        this.screen = tailAppend(this.screen, msg.data, SCREEN_MAX);
        for (const l of this.listeners) l(msg.data, false);
        return;
      case 'term':
        this.set({ term: msg.term });
        return;
      case 'exit':
        this.set({ exitCode: msg.exitCode, term: this.st.term ? { ...this.st.term, state: 'exited', exitCode: msg.exitCode } : null });
        return;
      case 'error':
        if (this.st.phase === 'open') this.opts.onNotice?.(msg.error);
        else this.set({ error: msg.error });
        return;
      default:
        return;
    }
  }

  private send(msg: TermClientMessage): void {
    if (this.ws && this.ws.readyState === 1) this.ws.send(JSON.stringify(msg));
  }

  subscribe(onData: Listener): () => void {
    this.listeners.add(onData);
    // A pane that mounts late (a tab switch) gets the screen so far.
    if (this.screen) onData(this.screen, true);
    return () => this.listeners.delete(onData);
  }

  input(data: string): void {
    if (!data) return;
    this.seq++;
    this.send({ t: 'in', seq: this.seq, data });
  }

  resize(cols: number, rows: number): void {
    this.send({ t: 'resize', cols, rows });
  }
}
